import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { shouldHideDuration } from "../utils/sessionValidation";
import PlatformIcon from "../../../common/components/Icon/PlatformIcon/PlatformIcon";

export interface SessionCardProps {
  id: number;
  date: Date;
  score: number | null;
  maxScore: number | null;
  duration: number | null; // in seconds
  progressionPercent?: number | null; // total_session_completion_percentage
  isCompleted: boolean;
  isSucceeded: boolean;
  userName?: string;
  userAvatarUrl?: string;
  onClick?: () => void;
  className?: string;
  averageScore?: number | null; // Average score for trend indicator
  hasGazeData?: boolean; // Whether gaze heatmap data is available
  targetPlatform?: string | null; // Platform the session was played on
  deviceModel?: string | null; // Device model (e.g., Quest 3, Pico 4)
}

const SessionCard = ({
  date,
  score,
  maxScore,
  duration,
  progressionPercent,
  isCompleted,
  isSucceeded,
  userName,
  userAvatarUrl,
  onClick,
  className = "",
  averageScore,
  hasGazeData,
  targetPlatform,
  deviceModel,
}: SessionCardProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "fr" ? fr : enUS;

  // Calculate trend (compare to average)
  const getTrend = (): number | null => {
    if (score == null || averageScore == null) return null;
    return score - averageScore;
  };
  const trend = getTrend();

  const formatDuration = (seconds: number | null): string => {
    if (seconds == null) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getScoreColor = (score: number | null, maxScore: number | null): string => {
    if (score == null || maxScore == null || maxScore === 0) return "text-gray-600";
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "text-green-600";
    if (percentage >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score: number | null, maxScore: number | null): string => {
    if (score == null || maxScore == null || maxScore === 0) return "bg-gray-100";
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "bg-green-100";
    if (percentage >= 50) return "bg-yellow-100";
    return "bg-red-100";
  };

  const getProgressionColor = (percent: number | null): string => {
    if (percent == null) return "text-gray-600";
    if (percent >= 80) return "text-green-600";
    if (percent >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  // Get card styling based on completion status
  const getCardStyles = () => {
    if (isCompleted) {
      return {
        border: "border-l-4 border-l-green-500 border-gray-200",
        bg: "bg-green-50/30",
      };
    }
    return {
      border: "border-l-4 border-l-orange-400 border-gray-200",
      bg: "bg-orange-50/20",
    };
  };

  const cardStyles = getCardStyles();

  return (
    <div
      onClick={onClick}
      className={`${cardStyles.bg} rounded-xl border ${
        cardStyles.border
      } p-4 shadow-sm transition-all duration-150 ${
        onClick ? "cursor-pointer hover:shadow-md hover:border-green-300" : ""
      } ${className}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {/* User avatar */}
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
            {userAvatarUrl ? (
              <img src={userAvatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            )}
          </div>

          {/* Date and user */}
          <div className="text-sm">
            <p className="font-medium text-gray-900">
              {format(date, "dd MMM yyyy", { locale })} - {format(date, "HH:mm", { locale })}
            </p>
            <p className="text-gray-500">{userName || t("statsNew.sessionDetail.anonymous")}</p>
          </div>
        </div>

        {/* Status badges */}
        <div className="flex items-center gap-2">
          {isCompleted ? (
            <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              {t("statsNew.session.completed")}
            </span>
          ) : (
            <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {t("statsNew.session.incomplete")}
            </span>
          )}

          {isSucceeded && (
            <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {t("statsNew.session.succeeded")}
            </span>
          )}
        </div>
      </div>

      {/* Score and Duration */}
      <div className="flex items-center gap-4">
        {/* Score with trend indicator */}
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg ${getScoreBgColor(
            score,
            maxScore,
          )}`}
        >
          <span className={`text-lg font-bold ${getScoreColor(score, maxScore)}`}>
            {score != null ? score.toFixed(1) : "N/A"}
          </span>
          {maxScore != null && <span className="text-sm text-gray-500">/ {maxScore}</span>}
          {/* Trend indicator */}
          {trend !== null && (
            <span
              className={`ml-1 text-sm font-bold ${
                trend > 0 ? "text-green-600" : trend < 0 ? "text-red-500" : "text-gray-400"
              }`}
              title={
                trend > 0
                  ? t("statsNew.session.aboveAverage")
                  : trend < 0
                  ? t("statsNew.session.belowAverage")
                  : t("statsNew.session.atAverage")
              }
            >
              {trend > 0 ? "↑" : trend < 0 ? "↓" : "−"}
            </span>
          )}
        </div>

        {/* Progression */}
        {progressionPercent != null && (
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <span className={`text-sm font-medium ${getProgressionColor(progressionPercent)}`}>
              {Math.round(progressionPercent)}%
            </span>
          </div>
        )}

        {/* Duration - hidden for old sessions with short duration */}
        <div className="flex items-center gap-2 text-gray-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm font-medium">
            {shouldHideDuration(date.toISOString(), duration) ? "-" : formatDuration(duration)}
          </span>
        </div>

        {/* Platform indicator */}
        <div className="relative group flex items-center">
          <PlatformIcon
            platform={targetPlatform || "Unknown"}
            iconProps={{ width: "20px", height: "20px", fill: "#6b7280" }}
          />
          {/* Tooltip */}
          <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
            {targetPlatform
              ? t(`statsNew.session.platform.${targetPlatform}`, { defaultValue: targetPlatform })
              : t("statsNew.session.platform.Unknown")}
            {deviceModel && <span className="block text-gray-300">{deviceModel}</span>}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
              <div className="w-2 h-2 bg-gray-800 rotate-45" />
            </div>
          </div>
        </div>

        {/* Gaze data indicator - next to duration for visibility */}
        {hasGazeData && (
          <div className="relative group flex items-center text-purple-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 cursor-help"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            {/* Tooltip */}
            <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              {t("statsNew.gazeHeatmap.dataAvailable")}
              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
                <div className="w-2 h-2 bg-gray-800 rotate-45" />
              </div>
            </div>
          </div>
        )}

        {/* Arrow */}
        {onClick && (
          <div className="ml-auto">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionCard;
